import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { default as AlgeriaNewsCarousel } from './NewsCarousel';
import { navigationItems } from '../data/navigation';
import SharedPageLayout from './SharedPageLayout';
import SocialMediaLinks from './shared/SocialMediaLinks';
import ContactEmail from './shared/ContactEmail';
import RotatingHero from './RotatingHero';
import SectionModal from './SectionModal';
import DidYouKnowSlider from './DidYouKnowSlider';


const LandingPageContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  background: ${theme.colors.white};
  overflow-x: hidden;
`;

const AboutSection = styled.section`
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  background: ${theme.colors.white};
  padding: 80px 5%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 40px 3%;
  }
`;


const HeritageSection = styled.section`
  width: 100%;
  padding: 64px 5%;
  background: ${theme.colors.light_gray};

  @media (max-width: 768px) {
    padding: 48px 4%;
  }

  @media (max-width: 480px) {
    padding: 32px 3%;
  }
`;

const HeritageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const SearchSection = styled.section`
  width: 100%;
  padding: 80px 5%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
            background: url('/shared_assets/images/pattern-bg.png') repeat;
    opacity: 0.05;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 40px 3%;
  }
`;

const SearchContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 30px;
  }

  @media (max-width: 480px) {
    gap: 24px;
  }
`;

const SearchTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.outchou_blue};
  text-align: justify;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const SearchSubtitle = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 20px;
  line-height: 30px;
  color: #666;
  text-align: justify;
  margin: 0;
  max-width: 1200px;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 27px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SearchWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  .instant-search-container {
    width: 100%;
  }

  .search-input {
    background: white;
    border: 2px solid #e0e0e0;
    border-radius: 50px;
    padding: 20px 60px 20px 24px;
    font-size: 18px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &:focus {
      border-color: ${theme.colors.outchou_blue};
      box-shadow: 0 6px 30px rgba(139, 69, 19, 0.2);
      transform: translateY(-2px);
    }

    &::placeholder {
      color: #999;
      font-style: italic;
    }
  }

  .search-icon {
    right: 24px;
  }

  .search-results {
    margin-top: 15px;
    border-radius: 20px;
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    .search-input {
      padding: 18px 55px 18px 20px;
      font-size: 16px;
    }
  }

  @media (max-width: 480px) {
    .search-input {
      padding: 16px 50px 16px 18px;
      font-size: 16px;
    }
  }
`;

const SearchCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const CategoryTag = styled.span`
  background: ${theme.colors.outchou_green};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #6B3410;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

const ContactSection = styled.section`
  width: 100%;
  padding: 64px 5%;
  background: ${theme.colors.light_beige};

  @media (max-width: 768px) {
    padding: 48px 4%;
  }

  @media (max-width: 480px) {
    padding: 32px 3%;
  }
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const ContactMainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 64px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 32px;
  }

  @media (max-width: 480px) {
    gap: 24px;
  }
`;

const ContactTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const ContactIcon = styled.img`
  width: 24px;
  height: 24px;

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const ContactText = styled.p`
  font-family: ${theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  color: ${theme.colors.outchou_blue};

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ContactAccroche = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 18px;
  line-height: 26px;
  color: ${theme.colors.outchou_blue};
  margin: 0;
  font-style: italic;

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SocialSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SocialText = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 16px;
  color: ${theme.colors.gray};
`;

const ContactButton = styled.button`
  background: ${theme.colors.outchou_green};
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 16px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;

  &:hover {
    background: ${theme.colors.outchou_green_hover};
  }

  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 14px;
  }
  color: #FFFFFF;

  @media (max-width: 768px) {
    width: 160px;
    height: 52px;
    font-size: 18px;
  }

  @media (max-width: 480px) {
    width: 140px;
    height: 48px;
    font-size: 16px;
  }
`;

const ContactImage = styled.div<{ $imageUrl: string }>`
  width: 400px;
  height: 300px;
  background: url(${props => props.$imageUrl}) center/cover;
  border-radius: 16px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 200px;
  }
`;

const AboutContent = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: stretch;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    
    > *:first-child {
      order: 1;
    }
    
    > *:last-child {
      order: 3;
    }
  }
`;

const AboutTextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const AboutTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.outchou_blue};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const AboutText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #666;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 26px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 24px;
  }
`;

const PlayButtonLarge = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 80px;
  background: ${theme.colors.outchou_green};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 24px solid white;
    border-top: 14px solid transparent;
    border-bottom: 14px solid transparent;
    margin-left: 4px;
  }

  &:hover {
    background: ${theme.colors.outchou_green_hover};
    transform: scale(1.1);
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;

    &::after {
      border-left-width: 20px;
      border-top-width: 12px;
      border-bottom-width: 12px;
    }
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;

    &::after {
      border-left-width: 18px;
      border-top-width: 10px;
      border-bottom-width: 10px;
    }
  }
`;

const VideoModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 999999;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 0px;
  backdrop-filter: blur(8px);
  animation: ${props => props.$isOpen ? 'fadeIn 0.3s ease-out' : 'none'};
  overflow: hidden;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    align-items: flex-start;
    padding-top: 80px;
  }
`;

const VideoModalContent = styled.div`
  position: relative;
  width: min(90vw, calc(100vh * 16 / 9));
  max-width: 1200px;
  height: min(100vh, calc(90vw * 9 / 16));
  max-height: 100vh;
  background: black;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 768px) {
    width: min(95vw, calc(100vh * 16 / 9));
    height: min(100vh, calc(95vw * 9 / 16));
    max-height: 100vh;
  }

  @media (max-width: 480px) {
    width: min(98vw, calc(100vh * 16 / 9));
    height: min(100vh, calc(98vw * 9 / 16));
    max-height: 100vh;
  }
`;

const VideoModalVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

// Modal vidéo mobile (structure identique à LogoVideoModal du Header)
const MobileVideoModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 999999;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const MobileVideoModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  background: black;
  border-radius: 8px;
  overflow: hidden;
`;

const MobileVideoModalVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
`;

const MobileVideoCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid white;
  border-radius: 50%;
  color: white;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  &::before {
    content: '×';
    line-height: 1;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid white;
  border-radius: 50%;
  color: white;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  &::before {
    content: '×';
    line-height: 1;
  }
`;

const AboutVideoContainer = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 600px;
  overflow: hidden;
  position: relative;
  background: url('/shared_assets/images/couverture-qui-sommes-nous.png') center/cover no-repeat;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    min-height: 400px;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }
`;

const AboutVideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 2;
`;

const AboutVideoContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 3;
  padding: 40px 5%;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  @media (max-width: 768px) {
    padding: 24px 4%;
  }

  @media (max-width: 480px) {
    padding: 20px 3%;
  }
`;

const AboutVideoTitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

const AboutVideoTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.white};
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const AboutVideoText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: ${theme.colors.white};
  margin: 0 0 16px 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 18px;
    margin-bottom: 8px;
  }
`;

const AboutVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  pointer-events: none;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;
  cursor: pointer;
  pointer-events: auto;

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 24px solid ${theme.colors.outchou_blue};
    border-top: 14px solid transparent;
    border-bottom: 14px solid transparent;
    margin-left: 4px;
  }

  ${AboutVideoContainer}:hover & {
    background: rgba(255, 255, 255, 1);
    transform: translate(-50%, -50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;

    &::after {
      border-left-width: 18px;
      border-top-width: 10px;
      border-bottom-width: 10px;
    }
  }

  @media (max-width: 480px) {
    width: 50px;
    height: 50px;

    &::after {
      border-left-width: 15px;
      border-top-width: 8px;
      border-bottom-width: 8px;
    }
  }
`;

const LogoCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 600px;
  overflow: hidden;
  background: ${theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    height: 400px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const LogoCarouselTrack = styled.div<{ $currentSlide: number; $totalSlides: number }>`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => `${props.$totalSlides * 100}%`};
  transform: translateX(${props => `calc(-${props.$currentSlide} * (100% / ${props.$totalSlides}))`});
  transition: transform 0.5s ease-in-out;
  height: 100%;
  flex-shrink: 0;
`;

const LogoCarouselSlide = styled.div<{ $totalSlides: number }>`
  flex: 0 0 ${props => `calc(100% / ${props.$totalSlides})`};
  width: ${props => `calc(100% / ${props.$totalSlides})`};
  min-width: ${props => `calc(100% / ${props.$totalSlides})`};
  max-width: ${props => `calc(100% / ${props.$totalSlides})`};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 30px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;


const LogoCarouselPagination = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 20px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    bottom: 15px;
    gap: 8px;
  }
`;

const LogoCarouselControls = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  z-index: 11;
  padding: 0 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoCarouselArrowButton = styled.button`
  width: 48px;
  height: 48px;
  background: ${theme.colors.white};
  border: none;
  border-radius: 50%;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: auto;

  &:hover {
    background: ${theme.colors.outchou_blue};
    transform: scale(1.1);
    
    img {
      filter: brightness(0) invert(1);
    }
  }

  img {
    width: 20px;
    height: 20px;
    transition: filter 0.3s ease;
  }
`;

const LogoCarouselDot = styled.button<{ $active: boolean }>`
  width: ${props => props.$active ? '12px' : '10px'};
  height: ${props => props.$active ? '10px' : '10px'};
  border-radius: 50%;
  border: 2px solid ${theme.colors.outchou_blue};
  background: ${props => props.$active ? theme.colors.outchou_blue : 'transparent'};
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  outline: none;

  &:hover {
    transform: scale(1.2);
    background: ${props => props.$active ? theme.colors.outchou_blue : 'rgba(68, 182, 157, 0.3)'};
  }

  &:focus {
    outline: 2px solid ${theme.colors.outchou_blue};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: ${props => props.$active ? '10px' : '8px'};
    height: ${props => props.$active ? '10px' : '8px'};
    border-width: 1.5px;
  }

  @media (max-width: 480px) {
    width: ${props => props.$active ? '8px' : '6px'};
    height: ${props => props.$active ? '8px' : '6px'};
    border-width: 1px;
  }
`;

const PlayButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: auto;

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    display: flex;
    justify-content: center;
    margin: 24px 0;
  }
`;

const RightTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.outchou_blue};
  margin: 0;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const RightText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #666;
  margin: 0;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 26px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 24px;
  }
`;


const SectionTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  color: ${theme.colors.outchou_blue};
  margin: 0;
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
`;

const HeritageCarousel = styled.div`
  display: flex;
  gap: 32px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 20px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 24px;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const HeritageCard = styled.div`
  min-width: 300px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    min-width: 250px;
  }

  @media (max-width: 480px) {
    min-width: 200px;
  }
`;

const CardImage = styled.div<{ $imageUrl: string }>`
  width: 100%;
  height: 200px;
  background: url(${props => props.$imageUrl}) center/cover;

  @media (max-width: 768px) {
    height: 150px;
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const CardContent = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const CardTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 22px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const CardText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #666;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 18px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

const CarouselControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    margin-top: 16px;
  }
`;

const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  background: white;
  border: 2px solid ${theme.colors.outchou_blue};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.colors.outchou_green};
    transform: scale(1.1);
  }

  img {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;

    img {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;

    img {
      width: 18px;
      height: 18px;
    }
  }
`;

// Bannière Outchou - Introduction
const OutchouBannerSection = styled.section`
  width: 100%;
  padding: 50px 5%;
  background: ${theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 40px 5%;
  }

  @media (max-width: 480px) {
    padding: 32px 4%;
  }
`;

// Wrapper pour "Le saviez-vous" à l'intérieur de AboutContent
const DidYouKnowInAboutWrapper = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    order: 2;
  }
`;

// Wrapper pour "Le saviez-vous" en dehors de AboutContent (desktop)
const DidYouKnowDesktopWrapper = styled.div`
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const OutchouBannerContent = styled.div`
  max-width: 900px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const OutchouBannerTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-size: 36px;
  font-weight: 700;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 24px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`;

const OutchouBannerText = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  line-height: 1.7;
  white-space: pre-line;
  color: ${theme.colors.outchou_blue};

  strong {
    font-weight: 700;
    background: linear-gradient(135deg, ${theme.colors.outchou_blue} 0%, #3A5A8A 35%, ${theme.colors.outchou_green} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    line-height: 1.5;
  }
`;

// Section "Vos mots pour Outchou"
const ContactCallToActionSection = styled.section`
  width: 100%;
  padding: 50px 5%;
  background: ${theme.colors.outchou_green};
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 40px 5%;
  }

  @media (max-width: 480px) {
    padding: 35px 4%;
  }
`;

const ContactCallToActionContent = styled.div`
  max-width: 800px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ContactCallToActionTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-size: 36px;
  font-weight: 700;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 12px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 8px;
  }
`;

const ContactCallToActionSubtitle = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 16px;
  font-weight: 400;
  color: ${theme.colors.white};
  margin: 0 0 24px 0;
  line-height: 1.5;
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 18px;
  }
`;

const ContactCallToActionButton = styled.button`
  font-family: ${theme.fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.outchou_blue};
  background: ${theme.colors.outchou_yellow};
  border: none;
  border-radius: 50px;
  padding: 12px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    background: ${theme.colors.outchou_yellow};
    opacity: 0.9;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 11px 28px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px 24px;
  }
`;

// Section de partage "Faire voyager Outchou"
const ShareSection = styled.section`
  width: 100%;
  padding: 50px 5%;
  background: ${theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 40px 5%;
  }

  @media (max-width: 480px) {
    padding: 35px 4%;
  }
`;

const ShareContent = styled.div`
  max-width: 800px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ShareText = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 24px;
  font-weight: 500;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 30px 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const ShareButton = styled.button`
  font-family: ${theme.fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.outchou_green};
  background: ${theme.colors.outchou_blue};
  border: none;
  border-radius: 50px;
  padding: 12px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    background: ${theme.colors.outchou_blue_hover};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 11px 28px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px 24px;
  }
`;

const Notification = styled.div<{ $show: boolean; $top: number; $left: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  transform: translateX(-50%) translateY(${props => props.$show ? '0' : '10px'});
  background: ${theme.colors.outchou_blue};
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: ${theme.fonts.body};
  font-size: 14px;
  font-weight: 500;
  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: ${props => props.$show ? 'auto' : 'none'};
  transition: opacity 0.3s ease, transform 0.3s ease;
  z-index: 99999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 12px;
  }
`;

// Images du carrousel "Un nom, un logo, un sens"
const logoSlides = [
  { src: '/shared_assets/images/1.png', alt: 'Logo Outchou 1' },
  { src: '/shared_assets/images/2.png', alt: 'Logo Outchou 2' },
  { src: '/shared_assets/images/3.png', alt: 'Logo Outchou 3' },
  { src: '/shared_assets/images/4.png', alt: 'Logo Outchou 4' },
  { src: '/shared_assets/images/5.png', alt: 'Logo Outchou 5' },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isAboutVideoModalOpen, setIsAboutVideoModalOpen] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const modalVideoRef = React.useRef<HTMLVideoElement>(null);
  const aboutVideoModalRef = React.useRef<HTMLVideoElement>(null);
  const aboutVideoRef = React.useRef<HTMLVideoElement>(null);
  const mobileAboutVideoRef = React.useRef<HTMLVideoElement>(null);
  const [isAboutVideoPlaying, setIsAboutVideoPlaying] = useState(false);
  const [isMobileAboutVideoOpen, setIsMobileAboutVideoOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const shareButtonRef = React.useRef<HTMLButtonElement>(null);
  const [notificationPosition, setNotificationPosition] = useState({ top: 0, left: 0 });
  const [logoCarouselIndex, setLogoCarouselIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [logoCarouselTouchStart, setLogoCarouselTouchStart] = useState<number | null>(null);
  const [logoCarouselTouchEnd, setLogoCarouselTouchEnd] = useState<number | null>(null);
  
  const heritageItems = navigationItems.slice(1).map(item => ({
    title: item.label,
    text: item.carousel_text,
            image: `/shared_assets/images/navigation/${item.id}.jpeg`,
    path: item.path
  }));

  const scrollCarousel = (direction: 'left' | 'right', className: string) => {
    const carousel = document.querySelector(`.${className}`);
    if (carousel) {
      const scrollAmount = 300;
      const currentScroll = carousel.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      carousel.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const handleAboutClick = () => {
    navigate('/about');
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handleHeritageCardClick = (path: string) => {
    navigate(path);
  };

  const goPrevLogoSlide = () => {
    setLogoCarouselIndex((prev) => (prev === 0 ? logoSlides.length - 1 : prev - 1));
  };

  const goNextLogoSlide = () => {
    setLogoCarouselIndex((prev) => (prev + 1) % logoSlides.length);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };


  // Démarrer la vidéo quand la modal s'ouvre et l'arrêter quand elle se ferme
  useEffect(() => {
    if (isVideoModalOpen && modalVideoRef.current) {
      modalVideoRef.current.play().catch(() => {
        // Ignorer les erreurs de lecture automatique
      });
    } else if (!isVideoModalOpen && modalVideoRef.current) {
      // S'assurer que la vidéo est bien arrêtée quand la modal est fermée
      modalVideoRef.current.pause();
      modalVideoRef.current.currentTime = 0;
      modalVideoRef.current.load(); // Recharger la vidéo pour s'assurer qu'elle ne joue plus
    }
  }, [isVideoModalOpen]);

  useEffect(() => {
    if (isAboutVideoModalOpen && aboutVideoModalRef.current) {
      aboutVideoModalRef.current.play().catch(() => {
        // Ignorer les erreurs de lecture automatique
      });
    }
    
    // Bloquer le scroll quand la modal vidéo est ouverte (desktop et mobile)
    if (isAboutVideoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isAboutVideoModalOpen]);

  // Détecter si on est sur mobile (même breakpoint que les media queries CSS: 768px)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Gestion du swipe pour le carrousel de logos sur mobile
  const minSwipeDistance = 50;

  const onLogoCarouselTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setLogoCarouselTouchEnd(null);
    setLogoCarouselTouchStart(e.targetTouches[0].clientX);
  };

  const onLogoCarouselTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    setLogoCarouselTouchEnd(e.targetTouches[0].clientX);
  };

  const onLogoCarouselTouchEnd = () => {
    if (!isMobile || !logoCarouselTouchStart || !logoCarouselTouchEnd) return;
    const distance = logoCarouselTouchStart - logoCarouselTouchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe gauche : slide suivant
      setLogoCarouselIndex((prev) => (prev + 1) % logoSlides.length);
    } else if (isRightSwipe) {
      // Swipe droite : slide précédent
      setLogoCarouselIndex((prev) => (prev === 0 ? logoSlides.length - 1 : prev - 1));
    }
  };

  const handleAboutVideoClick = () => {
    // Sur desktop, ouvrir la modal
    if (!isMobile) {
      setIsAboutVideoModalOpen(true);
    }
  };
  
  const handleAboutVideoPlayMobile = () => {
    // Sur mobile, ouvrir la modal vidéo mobile (structure identique au logo)
    setIsMobileAboutVideoOpen(true);
  };

  const handleCloseMobileAboutVideo = () => {
    setIsMobileAboutVideoOpen(false);
    if (mobileAboutVideoRef.current) {
      mobileAboutVideoRef.current.pause();
      mobileAboutVideoRef.current.currentTime = 0;
    }
  };

  // Démarrer la vidéo mobile quand la modal s'ouvre
  React.useEffect(() => {
    if (isMobileAboutVideoOpen && mobileAboutVideoRef.current) {
      mobileAboutVideoRef.current.play().catch(() => {});
    }
  }, [isMobileAboutVideoOpen]);

  const handleCloseAboutVideoModal = () => {
    setIsAboutVideoModalOpen(false);
    if (aboutVideoModalRef.current) {
      aboutVideoModalRef.current.pause();
      aboutVideoModalRef.current.currentTime = 0;
    }
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
  };

  const handleShareClick = async () => {
    const siteUrl = window.location.origin;
    
    // Calculer la position du bouton pour afficher la notification juste en dessous
    if (shareButtonRef.current) {
      const rect = shareButtonRef.current.getBoundingClientRect();
      setNotificationPosition({
        top: rect.bottom + 10,
        left: rect.left + rect.width / 2
      });
    }
    
    try {
      await navigator.clipboard.writeText(siteUrl);
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 2000);
    } catch (err) {
      // Fallback pour les navigateurs qui ne supportent pas l'API Clipboard
      const textArea = document.createElement('textarea');
      textArea.value = siteUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
        }, 2000);
      } catch (err) {
        console.error('Erreur lors de la copie:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Contenu de la modal "Un nom, un logo, un sens"
  const modalSections = {
    "Un nom": [
      "Outchou signifie « là-bas » en arabe algérien. Ce mot simple mais profond évoque l'idée d'un ailleurs, d'un horizon à explorer, d'une destination qui nous appelle.",
      "Il représente cette curiosité naturelle qui nous pousse à découvrir ce qui se trouve au-delà de notre quotidien, à explorer les richesses culturelles qui nous entourent."
    ],
    "Un logo": [
      "Notre identité visuelle s'inspire des céramiques traditionnelles algériennes, visibles notamment au musée du Bardo à Alger.",
      "Les couleurs choisies reflètent la diversité géographique et culturelle de l'Algérie :",
      "• Le bleu pour la mer Méditerranée et le ciel",
      "• Le vert turquoise pour l'eau cristalline et l'espoir",
      "• Le blanc pour la pureté et les bâtiments des villes côtières",
      "• Le jaune pour le soleil et l'immensité du Sahara",
      "Ces teintes fraîches et légères évoquent des éléments naturels qui nous unissent, provenant de différentes régions de notre pays."
    ],
    "Un sens": [
      "Outchou est né d'une volonté de préserver et de promouvoir la richesse culturelle algérienne.",
      "Notre mission est de créer un espace où chaque tradition, chaque plat, chaque mot raconte une histoire.",
      "Nous croyons que la culture algérienne est une mosaïque de siècles, de peuples et de cultures qui continuent de façonner notre identité aujourd'hui.",
      "À travers ce site, nous souhaitons faire découvrir au monde la beauté et la diversité de cette culture, tout en créant un pont entre le passé et le présent, entre le Nord et le Sud, entre toutes les régions qui composent l'Algérie."
    ]
  };

  const description = "Bienvenue sur Outchou, votre espace dédié à la richesse et à la diversité de la culture algérienne. Ce site rassemble l'héritage d'un pays aux multiples visages : traditions vestimentaires, savoir-faire artisanaux, gastronomie variée, musiques et danses populaires, mais aussi paysages contrastés allant de la Méditerranée au Sahara.";

  return (
    <>
    <SharedPageLayout
      backgroundImage="/shared_assets/images/navigation/Accueil.png"
      heroType="search"
      pageTitle="Accueil"
      pageDescription={description}
      customHeroSection={<RotatingHero />}
    >
      {/* Bannière Outchou - Introduction */}
      <OutchouBannerSection id="outchou-banner">
        <OutchouBannerContent>
          <OutchouBannerTitle>On vous raconte l'Algérie</OutchouBannerTitle>
          <OutchouBannerText>
            Outchou est une plateforme mettant en lumière la <strong>culture algérienne</strong> d'hier et d'aujourd'hui.{'\n'}
            Histoire, traditions et peuples, <strong>expliqués simplement</strong>.{'\n'}
            Pour <strong>apprendre, partager</strong> et se sentir connecté, partout dans le monde.
          </OutchouBannerText>
        </OutchouBannerContent>
      </OutchouBannerSection>

      {/* Bannière "Le saviez-vous ?" - Desktop uniquement */}
      <DidYouKnowDesktopWrapper>
        <DidYouKnowSlider />
      </DidYouKnowDesktopWrapper>
      
      <AboutSection>
        <AboutContent>
          {/* Colonne gauche : Vidéo */}
          <AboutVideoContainer onClick={isMobile ? handleAboutVideoPlayMobile : handleAboutVideoClick}>
            <AboutVideo
              ref={aboutVideoRef}
              src="/shared_assets/videos/qui-sommes-nous.mp4"
              playsInline
              muted
              controls={false}
              preload="auto"
            />
            <AboutVideoOverlay />
            <AboutVideoContent>
              <AboutVideoTitleContainer>
                <AboutVideoTitle>Qui sommes-nous ?</AboutVideoTitle>
              </AboutVideoTitleContainer>
              <AboutVideoText>
              Avant les couleurs, avant le logo… il y a eu une envie. Je t'explique.
              </AboutVideoText>
            </AboutVideoContent>
            <PlayButton onClick={(e) => {
              e.stopPropagation();
              if (isMobile) {
                handleAboutVideoPlayMobile();
              } else {
                handleAboutVideoClick();
              }
            }} />
          </AboutVideoContainer>

          {/* Bannière "Le saviez-vous ?" - Mobile uniquement (entre vidéo et carousel) */}
          <DidYouKnowInAboutWrapper>
            <DidYouKnowSlider />
          </DidYouKnowInAboutWrapper>

          {/* Colonne droite : Un nom, un logo, un sens - Carrousel d'images */}
          <LogoCarouselContainer
            onTouchStart={onLogoCarouselTouchStart}
            onTouchMove={onLogoCarouselTouchMove}
            onTouchEnd={onLogoCarouselTouchEnd}
          >
            <LogoCarouselTrack $currentSlide={logoCarouselIndex} $totalSlides={logoSlides.length}>
              {logoSlides.map((slide, index) => (
                <LogoCarouselSlide key={index} $totalSlides={logoSlides.length}>
                  <img src={slide.src} alt={slide.alt} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }} />
                </LogoCarouselSlide>
              ))}
            </LogoCarouselTrack>
            <LogoCarouselControls>
              <LogoCarouselArrowButton onClick={goPrevLogoSlide} aria-label="Slide précédente">
                <img src="/shared_assets/images/carousel_left.png" alt="Previous" />
              </LogoCarouselArrowButton>
              <LogoCarouselArrowButton onClick={goNextLogoSlide} aria-label="Slide suivante">
                <img src="/shared_assets/images/carousel_right.png" alt="Next" />
              </LogoCarouselArrowButton>
            </LogoCarouselControls>
            <LogoCarouselPagination>
              {Array.from({ length: logoSlides.length }).map((_, index) => (
                <LogoCarouselDot
                  key={index}
                  $active={index === logoCarouselIndex}
                  onClick={() => setLogoCarouselIndex(index)}
                  aria-label={`Aller à la slide ${index + 1}`}
                />
              ))}
            </LogoCarouselPagination>
          </LogoCarouselContainer>
        </AboutContent>
      </AboutSection>

      {/* Modal vidéo "Qui sommes-nous" style YouTube - Desktop uniquement */}
      {isAboutVideoModalOpen && !isMobile && (
        <VideoModal $isOpen={true} onClick={handleCloseAboutVideoModal}>
          <VideoModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseAboutVideoModal} />
            <VideoModalVideo
              ref={aboutVideoModalRef}
              src="/shared_assets/videos/qui-sommes-nous.mp4"
              controls
              autoPlay
              playsInline
              muted={false}
            />
          </VideoModalContent>
        </VideoModal>
      )}

      {/* Modal vidéo "Qui sommes-nous" - Mobile (structure identique au logo) */}
      <MobileVideoModal $isOpen={isMobileAboutVideoOpen} onClick={handleCloseMobileAboutVideo}>
        <MobileVideoModalContent onClick={(e) => e.stopPropagation()}>
          <MobileVideoCloseButton onClick={handleCloseMobileAboutVideo} />
          <MobileVideoModalVideo
            ref={mobileAboutVideoRef}
            src="/shared_assets/videos/qui-sommes-nous.mp4"
            controls
            playsInline
          />
        </MobileVideoModalContent>
      </MobileVideoModal>

      {/* Section "Vos mots pour Outchou" */}
      <ContactCallToActionSection>
        <ContactCallToActionContent>
          <ContactCallToActionTitle>Vos mots pour Outchou</ContactCallToActionTitle>
          <ContactCallToActionSubtitle>Vos idées, souvenirs et messages font vivre cette aventure</ContactCallToActionSubtitle>
          <ContactCallToActionButton onClick={() => navigate('/contact')}>
            Écrire à Outchou
          </ContactCallToActionButton>
        </ContactCallToActionContent>
      </ContactCallToActionSection>

     

      <AlgeriaNewsCarousel maxArticles={12} />

    </SharedPageLayout>
    
    {/* Notification "lien copié" - rendue via Portal directement dans le body */}
    {createPortal(
      <Notification $show={showNotification} $top={notificationPosition.top} $left={notificationPosition.left}>
        Lien copié
      </Notification>,
      document.body
    )}
    </>
  );
};

export default LandingPage; 